import {
  ArrowRight,
  ClipboardCheck,
  FileText,
  ListChecks,
  Mail,
  MessageSquare,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

/**
 * "Próximas ações da SME" — o que está esperando decisão, com o atalho para o
 * lugar onde a decisão acontece.
 *
 * TODOS OS QUATRO BOTÕES SÃO NAVEGAÇÃO, não gravação: levam para telas que já
 * existem no mapa (`nucleo/navegacao/mapa.ts`, `AREA_SME`). Por isso aparecem
 * sem aviso de indisponibilidade — o que eles prometem, eles cumprem.
 *
 * A linha acesa é a que tem item pendente. Sem pendência, ela fica igual às
 * outras: destaque permanente é destaque que ninguém mais enxerga.
 */

interface ProximaAcao {
  id: string;
  icone: typeof ListChecks;
  titulo: string;
  /** Quantos itens esperam. Zero vira a frase "Nenhum item pendente". */
  pendentes: number;
  rotuloDoBotao: string;
  destino: string;
}

interface ProximasAcoesProps {
  lotesAguardandoAprovacao: number;
  demandasEmAberto: number;
  documentosPendentes: number;
  mensagensNaoLidas: number;
}

export function ProximasAcoes({
  lotesAguardandoAprovacao,
  demandasEmAberto,
  documentosPendentes,
  mensagensNaoLidas,
}: ProximasAcoesProps) {
  const navegar = useNavigate();

  const acoes: ProximaAcao[] = [
    {
      id: 'aprovar-lotes',
      icone: ClipboardCheck,
      titulo: 'Aprovar lotes diários',
      pendentes: lotesAguardandoAprovacao,
      rotuloDoBotao: 'Abrir fila',
      destino: '/sme/apontamentos',
    },
    {
      id: 'responder-solicitacoes',
      icone: MessageSquare,
      titulo: 'Responder solicitações em aberto',
      pendentes: demandasEmAberto,
      rotuloDoBotao: 'Ver solicitações',
      destino: '/sme/demandas',
    },
    {
      id: 'validar-documentos',
      icone: FileText,
      titulo: 'Validar documentos de medições',
      pendentes: documentosPendentes,
      rotuloDoBotao: 'Validar docs',
      destino: '/sme/fechamento-mensal',
    },
    {
      id: 'mensagens',
      icone: Mail,
      titulo: 'Mensagens não lidas de credenciados',
      pendentes: mensagensNaoLidas,
      rotuloDoBotao: 'Abrir inbox',
      destino: '/sme/mensagens',
    },
  ];

  return (
    <Card className="rounded-2xl border border-border/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ListChecks className="h-4 w-4 text-primary" aria-hidden="true" />
          Próximas ações da SME
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {acoes.map((acao) => {
          const Icone = acao.icone;
          const pendente = acao.pendentes > 0;
          return (
            <div
              key={acao.id}
              className={cn(
                'flex flex-col gap-3 rounded-xl border border-border/60 p-3 sm:flex-row sm:items-center sm:justify-between',
                pendente && 'border-warning/50 bg-warning/5',
              )}
            >
              <div className="flex min-w-0 items-start gap-3">
                <Icone
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{acao.titulo}</p>
                  {pendente ? (
                    <Badge variant="outline" className="mt-1 font-mono text-[11px]">
                      {acao.pendentes} {acao.pendentes === 1 ? 'item' : 'itens'}
                    </Badge>
                  ) : (
                    <p className="text-xs text-muted-foreground">Nenhum item pendente</p>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => navegar(acao.destino)}
              >
                {acao.rotuloDoBotao}
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
